import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface Appointment {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  booking_reference: string;
  customer_id: number;
  lab_id: number;
  lab_branch_id: number;
  lab_test_id: number;
  product_checkup_id: number;
  appointment_date: string;
  time_slot_id: number;
  status: string;
  notes: string;
  cancellation_reason: string;
  payment_id: number;
  report_id: number;
  total_amount: number;
  home_collection: boolean;
  collection_address: string;
  estimated_report_date: string;
  completed_at: string;
  cancelled_at: string;
  tests: any[];
}

interface AppointmentsState {
  appointments: Appointment[];
  selectedAppointment: Appointment | null;
  loading: boolean;
  error: string | null;
}

const initialState: AppointmentsState = {
  appointments: [],
  selectedAppointment: null,
  loading: false,
  error: null,
};

export const fetchAppointments = createAsyncThunk(
  'appointments/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.ams}/api/v1/appointments`, {
        params: { per_page: 100 },
      });
      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || 'Failed to fetch appointments';
      console.error('[AMS] fetchAppointments failed:', msg);
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const fetchAppointmentById = createAsyncThunk(
  'appointments/fetchById',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.ams}/api/v1/appointments/${id}`);
      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || 'Failed to fetch appointment';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const updateAppointmentStatus = createAsyncThunk(
  'appointments/updateStatus',
  async ({ id, status }: { id: number; status: string }, thunkAPI) => {
    try {
      const response = await apiClient.put(`${config.services.ams}/api/v1/appointments/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || 'Failed to update status';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const confirmAppointment = createAsyncThunk(
  'appointments/confirm',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.ams}/api/v1/appointments/${id}/confirm`);
      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || 'Failed to confirm appointment';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const deleteAppointment = createAsyncThunk(
  'appointments/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.ams}/api/v1/appointments/${id}`);
      return id;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || 'Failed to delete appointment';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    clearSelectedAppointment: (state) => { state.selectedAppointment = null; },
    clearAppointmentsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointments.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAppointments.fulfilled, (state, action) => { state.loading = false; state.appointments = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchAppointments.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(fetchAppointmentById.fulfilled, (state, action) => { state.selectedAppointment = action.payload?.data || action.payload; })
      .addCase(updateAppointmentStatus.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.appointments.findIndex(a => a.ID === updated.ID);
          if (idx !== -1) state.appointments[idx] = updated;
        }
      })
      .addCase(confirmAppointment.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.appointments.findIndex(a => a.ID === updated.ID);
          if (idx !== -1) state.appointments[idx] = updated;
        }
      })
      .addCase(deleteAppointment.fulfilled, (state, action) => {
        state.appointments = state.appointments.filter(a => a.ID !== action.payload);
      });
  },
});

export const { clearSelectedAppointment, clearAppointmentsError } = appointmentsSlice.actions;
export default appointmentsSlice.reducer;
